import { Button, buttonVariants, H1, MultilineCode, P } from '@samera-one/ui';
import { useTranslation } from 'react-i18next';
import { getApiErrorCodes } from '~shared/api/api-client';

const API_ERROR_PREFIX = 'errors:api';

type ErrorHandlerProps = {
  error: Error;
  resetErrorBoundary?: (...args: unknown[]) => void;
};

export const ErrorHandler = (props: ErrorHandlerProps) => {
  const { error, resetErrorBoundary } = props;

  const { t, i18n } = useTranslation();

  // Decide on the codes: the messages are already translated text (or the backend's raw
  // detail), so looking them up as keys never matched and the guest saw only the generic
  // headline, e.g. for an offer that is no longer valid (samera/samera#3746).
  const errorCodes = getApiErrorCodes(error);

  const hasTranslatedErrors =
    errorCodes &&
    errorCodes.length > 0 &&
    errorCodes.every((code: string) =>
      i18n.exists(`${API_ERROR_PREFIX}.${code}`),
    );

  return (
    <main className="flex-1 flex justify-center items-center px-4">
      <div className="flex flex-col gap-4 items-center max-w-full text-center">
        <H1>{t('errors:somethingWentWrong')}</H1>
        {hasTranslatedErrors && (
          <P className="text-muted-foreground">
            {errorCodes.map((code: string) => (
              <span key={code}>
                {t(`${API_ERROR_PREFIX}.${code}`)}
                <br />
              </span>
            ))}
          </P>
        )}

        {import.meta.env.DEV && (
          <MultilineCode className="max-w-full">
            <pre>{error.stack}</pre>
          </MultilineCode>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={resetErrorBoundary}>
            {t('buttons.tryAgain')}
          </Button>
          <a href={'/'} type="button" className={buttonVariants()}>
            {t('buttons.toHomePage')}
          </a>
        </div>
      </div>
    </main>
  );
};
